import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { FilterDescriptor } from '@progress/kendo-data-query'
import { Observable } from 'rxjs'
import { take } from 'rxjs/operators'

import {
  Enums,
  ExpandedHuntPackageRecord,
  GeneratedContent,
  Hunt,
  HuntList,
  HuntPackageRecord,
  HuntPackagesAdded,
  HuntSummary,
  Subset,
} from '@cyborg-monorepo/hunter/common'

import { VerityEnvironmentService } from '../../../../../environments/verity-environment.service'
import { AuthService } from '../../../../auth/auth.service'
import { ExtendedState } from '../../../../modules/hunt/overview/hunt-list/hunt-state-service'
import { HuntUrlService } from '../../../../modules/hunt/overview/hunt-list/hunt-url-service'

/**
 * Service for interacting with the Hunt v2 REST API.
 *
 * Handles:
 * - Listing and paging hunts
 * - CRUD operations on individual hunts
 * - Archival and state transitions
 * - Generating and updating hunt report content
 *
 * All methods return strongly-typed Observables matching the API contracts.
 */
@Injectable({ providedIn: 'root' })
export class HuntAPIService {
  /** Angular HttpClient for HTTP requests */
  private readonly http = inject(HttpClient)

  /** Service to serialize/deserialize grid state into query params */
  private readonly urlService = inject(HuntUrlService)

  /** Verity environment service for API base URL */
  private readonly verityEnvironmentService = inject(VerityEnvironmentService)

  /** Used to attach the current access token to the beforeunload beacon */
  private readonly authService = inject(AuthService)

  /** Base URL for all Hunt v2 API calls */
  private readonly baseUrl = `${this.verityEnvironmentService.apiUrl}/v2/hunt`

  // ──────────────────────────────────────────────────────────
  // Hunt List & Summary Endpoints
  // ──────────────────────────────────────────────────────────

  /**
   * Retrieves a paginated list of hunts, with optional filtering and sorting.
   * @param state  The grid state containing skip, take, filter, sort, archived flag
   * @returns      Observable emitting a HuntList (items + meta)
   */
  public getHunts(state: ExtendedState): Observable<HuntList> {
    let params = new HttpParams()

    // Append filter descriptors to query params if provided
    if (state.filter?.filters?.length) {
      params = this.urlService.stateToHttpParams(
        state.filter.filters as FilterDescriptor[],
      )
    }

    // Append pagination (page, pageSize)
    const page = state.skip / state.take + 1
    params = params
      .set('page', String(page))
      .set('pageSize', String(state.take))

    // Append sorting (field, direction)
    if (state.sort?.length) {
      params = params
        .set('sortField', state.sort[0].field)
        .set('sortDir', state.sort[0].dir.toUpperCase())
    }

    // Append archived flag
    params = params.set('archived', String(state.archived))

    // Issue GET request to base URL with constructed params
    return this.http.get<HuntList>(this.baseUrl, { params })
  }

  /**
   * Fetches a lightweight summary of active hunts (no pagination).
   * @returns Observable emitting an array of HuntSummary
   */
  public getQuickHunts(): Observable<HuntSummary[]> {
    return this.http.get<HuntSummary[]>(`${this.baseUrl}/hunts`)
  }

  /**
   * Retrieves the total count of hunts.
   * @returns Observable emitting { result: number }
   */
  public getTotalHunts(): Observable<{ result: number }> {
    return this.http.get<{ result: number }>(`${this.baseUrl}/total-hunts`)
  }

  /**
   * Retrieves the count of completed hunts.
   * @returns Observable emitting { result: number }
   */
  public getCompletedHunts(): Observable<{ result: number }> {
    return this.http.get<{ result: number }>(`${this.baseUrl}/completed-hunts`)
  }

  // ──────────────────────────────────────────────────────────
  // Hunt Lifecycle Endpoints (CRUD + State Transitions)
  // ──────────────────────────────────────────────────────────

  /**
   * Creates a new Hunt record.
   * @param hunt  Partial Hunt payload
   * @returns     Observable emitting the created Hunt
   */
  public createHunt(hunt: Partial<Hunt>): Observable<Hunt> {
    return this.http.post<Hunt>(this.baseUrl, hunt)
  }

  /**
   * Retrieves a single Hunt by ID.
   * @param id  Hunt unique identifier
   * @returns   Observable emitting the Hunt
   */
  public getHuntById(id: string): Observable<Hunt> {
    return this.http.get<Hunt>(`${this.baseUrl}/${id}`)
  }

  /**
   * Updates basic fields of an existing Hunt.
   * @param id        Hunt ID to update
   * @param huntData  Partial payload of fields to update
   * @returns         Observable emitting the updated Hunt
   */
  public updateHunt(id: string, huntData: Partial<Hunt>): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}`, huntData)
  }

  /**
   * Fire-and-forget PUT used only from the `beforeunload` flush path.
   * Angular's HttpClient has no way to opt a request into `keepalive`, and
   * browsers are free to cancel an in-flight XHR/fetch once the page has
   * started unloading unless the request was made with `keepalive: true`
   * -- so this bypasses HttpClient and issues a raw `fetch` instead.
   * `navigator.sendBeacon` was considered but doesn't support custom
   * headers, so it can't carry the bearer auth token this endpoint
   * requires.
   */
  public updateHuntBeacon(id: string, huntData: Partial<Hunt>): void {
    let token = ''
    this.authService.accessToken$
      .pipe(take(1))
      .subscribe((current) => (token = current))

    fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(huntData),
    })
      .then((response) => {
        // fetch() only rejects on a network-level failure (DNS, connection
        // refused, etc.) -- an HTTP error response (e.g. a stale-token 401)
        // still resolves normally, so that case has to be checked
        // explicitly rather than relying on .catch() below.
        if (!response.ok) {
          console.error(
            'HuntAPIService.updateHuntBeacon',
            `Beacon save failed with status ${response.status}`,
          )
        }
        return response
      })
      .catch((error) => {
        console.error('HuntAPIService.updateHuntBeacon', error)
      })
  }

  /**
   * Updates only the status (e.g. OPEN, DONE) of a Hunt.
   * @param id             Hunt ID
   * @param statusPayload  { status: Enums.StatusEnum }
   * @returns              Observable emitting the updated Hunt
   */
  public updateHuntStatus(
    id: string,
    statusPayload: { status: Enums.StatusEnum },
  ): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}/status`, statusPayload)
  }

  /**
   * Archives a Hunt, removing it from the default list.
   * @param id  Hunt ID to archive
   * @returns   Observable emitting the archived Hunt
   */
  public archiveHunt(id: string): Observable<Hunt> {
    return this.http.get<Hunt>(`${this.baseUrl}/${id}/archive`)
  }

  /**
   * Unarchives a Hunt, restoring it to active lists.
   * @param id  Hunt ID to unarchive
   * @returns   Observable emitting the unarchived Hunt
   */
  public unarchiveHunt(id: string): Observable<Hunt> {
    return this.http.get<Hunt>(`${this.baseUrl}/${id}/unarchive`)
  }

  /**
   * Marks a Hunt as open.
   * @param id  Hunt ID
   * @returns   Observable emitting the updated Hunt
   */
  public openHunt(id: string): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}/open`, null)
  }

  /**
   * Marks a Hunt as closed.
   * @param id  Hunt ID
   * @returns   Observable emitting the updated Hunt
   */
  public closeHunt(id: string): Observable<Hunt> {
    return this.http.get<Hunt>(`${this.baseUrl}/${id}/close`)
  }

  /**
   * Finalizes the report for a Hunt.
   * @param id  Hunt ID
   * @returns   Observable emitting the finalized Hunt
   */
  public finalizeHuntReport(id: string): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}/finalize`, null)
  }

  // ──────────────────────────────────────────────────────────
  // Hunt Reporting Endpoints
  // ──────────────────────────────────────────────────────────

  /**
   * Saves a subset of Hunt fields as a draft report.
   * @param id     Hunt ID
   * @param report Partial fields to save
   * @returns      Observable emitting the updated Hunt
   */
  public saveHuntReport(id: string, report: Subset<Hunt>): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}/save-report`, report)
  }

  /**
   * Updates only the notes field of a Hunt.
   * @param id            Hunt ID
   * @param notesPayload  { notes: string }
   * @returns             Observable emitting the updated Hunt
   */
  public updateHuntNotes(
    id: string,
    notesPayload: { notes: string },
  ): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}/notes`, notesPayload)
  }

  // ──────────────────────────────────────────────────────────
  // Content & Package Operations
  // ──────────────────────────────────────────────────────────

  /**
   * Generates content for specified Hunt packages.
   * @param payload  { huntPackages: ExpandedHuntPackageRecord[] }
   * @param id       Hunt ID
   * @returns        Observable emitting the updated Hunt
   */
  public generateHuntContent(
    payload: { huntPackages: ExpandedHuntPackageRecord[] },
    id: string,
  ): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}/generate`, payload)
  }

  /**
   * Adds packages to an existing Hunt's quick summary.
   * @param id        Hunt ID
   * @param packages  Array of HuntPackageRecord
   * @returns         Observable emitting HuntPackagesAdded
   */
  public addHuntPackagesToQuickHunt(
    id: string,
    packages: HuntPackageRecord[],
  ): Observable<HuntPackagesAdded> {
    return this.http.put<HuntPackagesAdded>(
      `${this.baseUrl}/${id}/add-hunt-packages`,
      packages,
    )
  }

  /**
   * Updates a specific piece of generated content within a Hunt.
   * @param id       Hunt ID
   * @param context  Context object including content and identifiers
   * @returns        Observable emitting the updated Hunt
   */
  public updateHuntContent(
    id: string,
    context: {
      content: GeneratedContent
      huntPackageUUID: string
      environment: string
      tool: string
      category: string
    },
  ): Observable<Hunt> {
    return this.http.put<Hunt>(`${this.baseUrl}/${id}/content`, context)
  }
}
